'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import { Checkbox } from '@sgcmpteltd/uikit-react';
import cx from 'clsx';
import Link from 'next/link';
import { useMemo, useState } from 'react';

import {
  ListTypeLabel,
// eslint-disable-next-line @stylistic/max-len
} from '@/app/(routes)/listing/_components/page-content/search-list/car-card/listing-type-label';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { joinSgcmSite } from '@/utils/url/join/public';

import {
  IMAGE_HEIGHT,
  IMAGE_MOBILE_HEIGHT,
  IMAGE_MOBILE_WIDTH,
  IMAGE_WIDTH,
  LIST_TYPE_LABEL_SIZE,
  SGCM_IMAGE_URL,
} from './constants';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SimilarNewCarContent: Component = ({
  adList,
  similarNewCars,
  compareItems,
  onComparedCheckboxChange,
}) => {
  const isComparedSubmodelMap = useMemo(() => similarNewCars
    .reduce((acc, car) => ({
      ...acc,
      [car.id]: car.subModels.map(subModel => compareItems
        .some(compareItem => compareItem.submodelId === subModel.id)),
    }), {} as Record<number, boolean[]>), [compareItems, similarNewCars]);

  const { isDesktop } = useScreenDetect();
  const isDesktopMode = isDesktop();

  const imageWidth = isDesktopMode ? IMAGE_WIDTH : IMAGE_MOBILE_WIDTH;
  const imageHight = isDesktopMode ? IMAGE_HEIGHT : IMAGE_MOBILE_HEIGHT;

  const [hoveredId, setHoveredId] = useState<number | null>(null);

  return (
    <main>
      <div className={styles.containerSubModelCompare}>
        <div className={styles.textTitle}>
          Select a sub model for comparison
        </div>
        {similarNewCars.map((similarNewCar) => {
          const minPrice = Math.min(
            ...similarNewCar.subModels
              .map(subModel => subModel.price ?? Infinity),
          );

          const handleCheckboxChange = (submodelId: number, index: number) => {
            onComparedCheckboxChange?.(
              {
                id: similarNewCar.id,
                submodelId,
                make: similarNewCar.make,
                model: similarNewCar.model,
                name: similarNewCar.subModels[index].name,
                thumbnailUrl: `https://i.i-sgcm.com/new_cars/cars/${similarNewCar.id}/${similarNewCar.id}_m.jpg`,
              },
              !isComparedSubmodelMap[similarNewCar.id][index],
            );
          };

          return (
            <div
              key={similarNewCar.id}
              className={styles.containerSubModel}>
              <div className={styles.containerImageAndInfo}>
                <div
                  className={styles.carImage}>
                  <Link
                    className={styles.containerImage}
                    href={`${joinSgcmSite(`new-cars/info/${similarNewCar.id}/${similarNewCar.slug}`)}`}
                    onMouseEnter={() => setHoveredId(similarNewCar.id)}
                    onMouseLeave={() => setHoveredId(null)}>
                    <ImageWithFallback
                      alt={`${similarNewCar.id}-image`}
                      className={styles.imageVehicle}
                      height={imageHight}
                      src={`${SGCM_IMAGE_URL}/${similarNewCar.id}/${similarNewCar.id}_m.jpg`}
                      width={imageWidth} />
                  </Link>
                </div>
                <div>
                  <Link
                    className={cx(
                      styles.containerCarName,
                      hoveredId === similarNewCar.id && styles.hovered,
                    )}
                    href={`${joinSgcmSite(`new-cars/info/${similarNewCar.id}/${similarNewCar.slug}`)}`}>
                    <div className={styles.textMakeAndModel}>
                      {similarNewCar.make}
                      {' '}
                      {similarNewCar.model}
                    </div>
                  </Link>
                  <div className={styles.textFromPrice}>
                    From $
                    {minPrice.toLocaleString()}
                  </div>
                  <div className={styles.containerDealersName}>
                    { similarNewCar.authorizedDealers.map(dealer => (
                      <Link
                        key={dealer.id}
                        className={styles.linkDealerName}
                        href={joinSgcmSite(`/new-cars/listing/search?dealer_id=${dealer.id}`)}>
                        {dealer.name}
                      </Link>
                    )) }
                  </div>
                </div>
              </div>
              <div className={styles.containerCheckBox}>
                {similarNewCar.subModels.map((subModel, idx) => {
                  const checkboxId = `checkbox-${subModel.id}`;
                  const links = [
                    { href: `${joinSgcmSite(`/new_cars/newcars_specs.php?CarCode=${similarNewCar.id}&Subcode=${subModel.id}`)}`, label: 'Specs' },
                    { href: `${joinSgcmSite(`/new_cars/newcars_features.php?CarCode=${similarNewCar.id}&Subcode=${subModel.id}`)}`, label: 'Features' },
                  ];

                  if (!adList) {
                    links.push({ href: `${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${similarNewCar.id}&Subcode=${subModel.id}`)}`, label: 'Pricelist' });
                  }
                  return (
                    <div
                      key={subModel.id}
                      className={styles.checkBoxContent}>
                      <div className={styles.containerCheck}>
                        <Checkbox
                          checked={isComparedSubmodelMap[similarNewCar.id][idx]}
                          id={checkboxId}
                          size={16}
                          value={subModel.id}
                          onChange={() => handleCheckboxChange(
                            subModel.id,
                            idx,
                          )} />
                      </div>
                      <div className={styles.containerName}>
                        <button
                          className={styles.textName}
                          onClick={() => handleCheckboxChange(
                            subModel.id,
                            idx,
                          )}>
                          {subModel.name}
                          {' '}
                          (
                          {subModel.transmissionType}
                          )
                        </button>
                        {subModel.isFacelift && (
                          <ListTypeLabel
                            labelSize={LIST_TYPE_LABEL_SIZE}
                            type="facelift" />
                        )}
                        {subModel.isNewVariant && (
                          <ListTypeLabel
                            labelSize={LIST_TYPE_LABEL_SIZE}
                            type="newSubmodel" />
                        )}
                      </div>
                      <div className={styles.containerTooltip}>
                        {links.map(({ href, label }, i) => (
                          <div key={`tooltip-${label}`}>
                            <a
                              href={href}
                              rel="noreferrer noopener"
                              target="_blank">
                              {label}
                            </a>
                            <span className={cx(
                              styles.linkSeparate,
                              i === links.length - 1 && styles.isLastLink, // @suppress longLineCheck
                            )}>
                              {i < links.length - 1 && '|'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </main>
  );
};
